import React from "react";
import { motion } from "framer-motion";

const TAPE_CLASSES = ["tape-rotate-1", "tape-rotate-2", "tape-rotate-3", "tape-rotate-4"];

const LookbookCard = ({ tee, index, total }) => {
  const tapeClass = TAPE_CLASSES[index % TAPE_CLASSES.length];

  // Bento layout — alternating column spans for asymmetry
  const spanClasses = [
    "md:col-span-7",
    "md:col-span-5",
    "md:col-span-5",
    "md:col-span-7",
  ];

  // Alternate card backgrounds
  const bgs = ["#FFFDF5", "#FDE047", "#FFFDF5", "#FDE047"];
  const bg = bgs[index % bgs.length];

  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className={`col-span-1 ${spanClasses[index % spanClasses.length]} border-4 border-[#1C1914] shadow-hard relative`}
      style={{ backgroundColor: bg }}
      data-testid={`product-card-${tee.id}`}
    >
      {/* Image */}
      <div className="border-b-2 border-[#1C1914] bg-[#FFFDF5] overflow-hidden">
        <img
          src={tee.image_url}
          alt={tee.name}
          className="w-full h-[340px] md:h-[420px] object-cover hover-wiggle"
          data-testid={`product-image-${tee.id}`}
          loading="lazy"
        />
      </div>

      {/* Status sticker */}
      <span
        className={`sticker absolute -top-3 -right-3 ${tapeClass} z-10`}
        data-testid={`product-status-${tee.id}`}
      >
        {tee.status}
      </span>

      {/* Body */}
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <h3
            className="font-display text-4xl md:text-5xl uppercase leading-none tracking-tight"
            data-testid={`product-name-${tee.id}`}
          >
            {tee.name}
          </h3>
          <span
            className="inline-block w-6 h-6 border-2 border-[#1C1914] shrink-0 mt-2"
            style={{ backgroundColor: tee.color_chip }}
            aria-label={`color chip ${tee.color_chip}`}
            data-testid={`product-color-${tee.id}`}
          />
        </div>

        <p
          className="mt-3 font-condensed text-xl md:text-2xl text-[#4A5D23] uppercase tracking-wide leading-tight"
          data-testid={`product-tagline-${tee.id}`}
        >
          {tee.tagline}
        </p>

        <p
          className="mt-5 font-body text-base md:text-lg leading-relaxed text-[#1C1914]/80 max-w-prose"
          data-testid={`product-description-${tee.id}`}
        >
          {tee.description}
        </p>

        <div className="mt-6 flex items-center justify-between border-t-2 border-[#1C1914] pt-4">
          <span className="font-condensed text-lg uppercase tracking-widest">
            № 0{index + 1} / {String(total).padStart(2, "0")}
          </span>
          <span className="font-condensed text-lg uppercase tracking-widest text-[#D95D39]">
            Heavyweight cotton • Hand-screened
          </span>
        </div>
      </div>
    </motion.article>
  );
};

const Lookbook = ({ tees }) => {
  return (
    <section
      id="lookbook"
      className="relative px-5 md:px-10 py-20 md:py-28 border-b-4 border-[#1C1914]"
      data-testid="lookbook-section"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <p className="font-condensed text-lg uppercase tracking-widest text-[#D95D39]" data-testid="lookbook-eyebrow">
            // Drop 001 — The Sour Collection
          </p>
          <h2 className="font-display text-5xl md:text-7xl uppercase leading-none mt-3" data-testid="lookbook-title">
            The Lookbook
          </h2>
        </div>
        <p className="max-w-md font-body text-base md:text-lg text-[#1C1914]/80" data-testid="lookbook-description">
          Five shirts. Zero focus groups. Each one drawn by a person who probably should have been doing something else.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8" data-testid="lookbook-grid">
        {tees.map((tee, i) => (
          <LookbookCard key={tee.id} tee={tee} index={i} total={tees.length} />
        ))}
      </div>

      {/* Side note */}
      <div className="mt-14 flex flex-wrap items-center justify-between gap-4 border-t-2 border-dashed border-[#1C1914] pt-6">
        <p className="font-condensed text-xl uppercase tracking-wide" data-testid="lookbook-footnote">
          All designs are works in progress, much like the people wearing them.
        </p>
        <span className="sticker tape-rotate-3" data-testid="lookbook-batch-tag">small batch / large regret</span>
      </div>
    </section>
  );
};

export default Lookbook;
